// #212 PR-B — Zod-free validator for the OnCatch.identify() payload.
//
// This file MUST NOT import Zod. The widget bundle imports the validator
// here to enforce the same rules the server-side Zod schema does, but
// without paying Zod's tree-shaken footprint (~5-7 KB gzipped on top of
// the widget's 80 KB cap).
//
// Sibling file identified-reporter-schema.ts hosts the Zod version for
// admin/server use. Both validators share the constants + reserved-key
// walker from this file so they reject the same inputs by construction.
//
// Design history: 5 rounds of codex adversarial review. See
// /tmp/oncatch-212-pr-bc-design-v5.md for the full disposition trail.

// =============================================================================
// Shared constants
// =============================================================================

export const IDENTIFIED_REPORTER_LIMITS = {
  USER_ID_MAX: 200,
  EMAIL_MAX: 254,
  NAME_MAX: 200,
  PLAN_MAX: 100,
  CUSTOM_MAX_KEYS: 50,
  CUSTOM_MAX_BYTES: 8192,
  CUSTOM_MAX_DEPTH: 16,
  CUSTOM_MAX_NODES: 1000,
} as const;

// Prototype-pollution vectors. JS object key namespace; reject these at
// any depth in `custom` to prevent the widget or admin code (which reads
// the stored JSON back into JS) from accidentally polluting prototypes.
export const RESERVED_KEYS: ReadonlySet<string> = new Set([
  '__proto__',
  'constructor',
  'prototype',
  '__defineGetter__',
  '__defineSetter__',
  '__lookupGetter__',
  '__lookupSetter__',
]);

// Tightened to match Zod 4 rejection behavior on common adversarial inputs:
// consecutive dots in local part, dash-bordered domain labels, consecutive
// dots in domain, missing TLD, etc. Not RFC-5322 strict (acceptable per
// the spec — we cap at 254 chars and let bad-but-syntactically-valid
// addresses through; real validation is the email server's job).
const EMAIL_RE =
  /^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// =============================================================================
// Types
// =============================================================================

export interface IdentifiedReporter {
  userId: string;
  email?: string;
  name?: string;
  plan?: string;
  custom?: Record<string, unknown>;
}

export interface ValidationError {
  path: string;
  reason: string;
}

export type ValidationResult =
  | { ok: true; data: IdentifiedReporter }
  | { ok: false; errors: ValidationError[] };

// =============================================================================
// Cycle-safe iterative walker
// =============================================================================
// Fail-closed: returns true (treat as rejecting payload) on any of:
//   - reserved key encountered at any depth
//   - depth > CUSTOM_MAX_DEPTH
//   - node count > CUSTOM_MAX_NODES
//   - hostile proxy / getter throws on property access
//   - cycle detected (WeakSet)

export function containsReservedKeyOrTooDeep(value: unknown): boolean {
  let visited: WeakSet<object>;
  try {
    visited = new WeakSet<object>();
  } catch {
    return true;
  }
  const stack: Array<{ node: unknown; depth: number }> = [{ node: value, depth: 0 }];
  let nodeCount = 0;

  while (stack.length > 0) {
    const item = stack.pop();
    if (!item) break;
    const { node, depth } = item;
    if (depth > IDENTIFIED_REPORTER_LIMITS.CUSTOM_MAX_DEPTH) return true;
    if (++nodeCount > IDENTIFIED_REPORTER_LIMITS.CUSTOM_MAX_NODES) return true;
    if (node === null || typeof node !== 'object') continue;
    try {
      // Cycle detected → reject (fail closed). Cycles can't appear in
      // JSON input, but live JS objects passed to widget identify() can.
      if (visited.has(node)) return true;
      visited.add(node);
      if (Array.isArray(node)) {
        for (const child of node) stack.push({ node: child, depth: depth + 1 });
      } else {
        for (const key of Object.keys(node)) {
          if (RESERVED_KEYS.has(key)) return true;
          stack.push({ node: (node as Record<string, unknown>)[key], depth: depth + 1 });
        }
      }
    } catch {
      // Hostile proxy / getter / non-enumerable trap → fail closed
      return true;
    }
  }
  return false;
}

// =============================================================================
// Hand-rolled validator (Zod-free)
// =============================================================================

const ALLOWED_TOP_LEVEL_KEYS = new Set(['userId', 'email', 'name', 'plan', 'custom']);

export function validateIdentifiedReporter(input: unknown): ValidationResult {
  const errors: ValidationError[] = [];
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, errors: [{ path: '', reason: 'invalid_type' }] };
  }
  const obj = input as Record<string, unknown>;

  // Reject unknown top-level keys (mirrors Zod's .strict()).
  for (const key of Object.keys(obj)) {
    if (!ALLOWED_TOP_LEVEL_KEYS.has(key)) {
      errors.push({ path: key, reason: 'unrecognized_key' });
    }
  }

  // userId — required string, 1-200 chars
  const userIdRaw = typeof obj.userId === 'string' ? obj.userId.trim() : undefined;
  if (!userIdRaw || userIdRaw.length === 0) {
    errors.push({ path: 'userId', reason: 'required' });
  } else if (userIdRaw.length > IDENTIFIED_REPORTER_LIMITS.USER_ID_MAX) {
    errors.push({ path: 'userId', reason: 'too_long' });
  }

  // email — optional, regex-tight, ≤254, lowercased
  let emailNormalized: string | undefined;
  if (obj.email !== undefined) {
    if (typeof obj.email !== 'string') {
      errors.push({ path: 'email', reason: 'invalid_type' });
    } else {
      const trimmed = obj.email.trim().toLowerCase();
      if (!EMAIL_RE.test(trimmed)) {
        errors.push({ path: 'email', reason: 'invalid_email' });
      } else if (trimmed.length > IDENTIFIED_REPORTER_LIMITS.EMAIL_MAX) {
        errors.push({ path: 'email', reason: 'too_long' });
      } else {
        emailNormalized = trimmed;
      }
    }
  }

  // name — optional, ≤200
  let nameNormalized: string | undefined;
  if (obj.name !== undefined) {
    if (typeof obj.name !== 'string') {
      errors.push({ path: 'name', reason: 'invalid_type' });
    } else {
      const trimmed = obj.name.trim();
      if (trimmed.length > IDENTIFIED_REPORTER_LIMITS.NAME_MAX) {
        errors.push({ path: 'name', reason: 'too_long' });
      } else {
        nameNormalized = trimmed;
      }
    }
  }

  // plan — optional, ≤100
  let planNormalized: string | undefined;
  if (obj.plan !== undefined) {
    if (typeof obj.plan !== 'string') {
      errors.push({ path: 'plan', reason: 'invalid_type' });
    } else {
      const trimmed = obj.plan.trim();
      if (trimmed.length > IDENTIFIED_REPORTER_LIMITS.PLAN_MAX) {
        errors.push({ path: 'plan', reason: 'too_long' });
      } else {
        planNormalized = trimmed;
      }
    }
  }

  // custom — optional object, ≤50 keys, ≤8 KB serialized, no reserved keys
  let customNormalized: Record<string, unknown> | undefined;
  if (obj.custom !== undefined) {
    if (typeof obj.custom !== 'object' || obj.custom === null || Array.isArray(obj.custom)) {
      errors.push({ path: 'custom', reason: 'invalid_type' });
    } else {
      const c = obj.custom as Record<string, unknown>;
      let keyCount = 0;
      try {
        keyCount = Object.keys(c).length;
      } catch {
        errors.push({ path: 'custom', reason: 'invalid_type' });
      }
      if (keyCount > IDENTIFIED_REPORTER_LIMITS.CUSTOM_MAX_KEYS) {
        errors.push({ path: 'custom', reason: 'too_many_keys' });
      } else if (containsReservedKeyOrTooDeep(c)) {
        errors.push({ path: 'custom', reason: 'reserved_key' });
      } else {
        let serialized = '';
        try {
          serialized = JSON.stringify(c);
        } catch {
          errors.push({ path: 'custom', reason: 'oversize' });
        }
        if (serialized.length > IDENTIFIED_REPORTER_LIMITS.CUSTOM_MAX_BYTES) {
          errors.push({ path: 'custom', reason: 'oversize' });
        } else if (errors.find((e) => e.path === 'custom') === undefined) {
          customNormalized = c;
        }
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    data: {
      userId: userIdRaw as string,
      email: emailNormalized,
      name: nameNormalized,
      plan: planNormalized,
      custom: customNormalized,
    },
  };
}
